import React, { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { NAV_SECTIONS } from "@/lib/nav";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Menu, LogOut, User, ChevronsLeft, Printer } from "lucide-react";

const ROLE_LABELS = {
  admin: "Administrator", sales_executive: "Sales Executive", sales_manager: "Sales Manager",
  design: "Design / Prepress", production_planner: "Production Planner", production_operator: "Production Operator",
  qc_inspector: "QC Inspector", store: "Store / Inventory", purchase: "Purchase Officer",
  accounts: "Accounts", hr: "HR",
};

function initials(name = "") {
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

export default function AppShell() {
  const { user, logout, can } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const visibleSections = NAV_SECTIONS
    .map((s) => ({ ...s, items: s.items.filter((i) => can(i.module, "view")) }))
    .filter((s) => s.items.length > 0);

  const Sidebar = (
    <aside
      className={`flex h-full flex-col bg-slate-950 text-slate-300 transition-[width] duration-200 ${collapsed ? "w-16" : "w-64"}`}
      data-testid="app-sidebar"
    >
      <div className="flex h-16 items-center gap-2 border-b border-slate-800 px-4">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-indigo-600 text-white">
          <Printer className="h-4 w-4" />
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <p className="truncate font-heading text-sm font-bold text-white">Nayara Digital Prints</p>
            <p className="truncate text-[11px] text-slate-400">ERP System</p>
          </div>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto px-2 py-4">
        {visibleSections.map((section) => (
          <div key={section.title} className="mb-4">
            {!collapsed && (
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{section.title}</p>
            )}
            {section.items.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`nav-${item.module}`}
                  className={({ isActive }) =>
                    `mb-0.5 flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-150 ${
                      isActive ? "bg-indigo-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                    }`
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>
      <button
        onClick={() => setCollapsed((c) => !c)}
        data-testid="sidebar-collapse-btn"
        className="hidden items-center gap-2 border-t border-slate-800 px-4 py-3 text-xs text-slate-400 hover:text-white lg:flex"
      >
        <ChevronsLeft className={`h-4 w-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
        {!collapsed && "Collapse"}
      </button>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      <div className="hidden lg:block">{Sidebar}</div>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
        </div>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 md:px-6">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5 text-slate-700" />
            </button>
            <h1 className="font-heading text-lg font-bold tracking-tight text-slate-800">
              {NAV_SECTIONS.flatMap((s) => s.items).find((i) => location.pathname.startsWith(i.to))?.label
                || (location.pathname.startsWith("/users") || location.pathname.startsWith("/roles") || location.pathname.startsWith("/audit") ? "User Management" : "Nayara ERP")}
            </h1>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-slate-100" data-testid="user-menu-btn">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-indigo-100 text-xs font-semibold text-indigo-700">{initials(user?.name)}</AvatarFallback>
                </Avatar>
                <div className="hidden text-left sm:block">
                  <p className="text-sm font-medium leading-tight text-slate-800">{user?.name}</p>
                  <p className="text-[11px] leading-tight text-slate-500">{ROLE_LABELS[user?.role] || user?.role}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs font-normal text-slate-500">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <NavLink to="/profile" data-testid="menu-profile"><User className="mr-2 h-4 w-4" />My Profile</NavLink>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-rose-600 focus:text-rose-600">
                <LogOut className="mr-2 h-4 w-4" />Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          <div className="mx-auto max-w-[1400px] animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
